import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation, Trans } from "react-i18next";
import { Type, Upload, Sparkles, FileText, X, BookmarkCheck, Trash2 } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import WizardSteps from "@/components/WizardSteps";
import ComingSoonModal from "@/components/ComingSoonModal";
import PlaceholderEditor from "@/components/PlaceholderEditor";
import { Field, TextInput, TextArea } from "@/components/FormField";
import { useBooking } from "@/context/BookingContext";
import { api, formatApiError } from "@/lib/api";
import { SAMPLE_NOTICE_IMAGES } from "@/lib/brand";

const PROFESSIONS = [
  "Advocate",
  "Business Owner",
  "Builder / Developer",
  "Bank / Financial Institution",
  "Government / PSU",
  "Individual",
  "Other",
];

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

const GST_PERCENT = 5;

export default function ComposerStep() {
  const nav = useNavigate();
  const { t } = useTranslation();
  const { draft, update, updateAdvocate } = useBooking();
  const [papers, setPapers] = useState([]);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);
  const [adSizeModalOpen, setAdSizeModalOpen] = useState(false);
  const [editionModalOpen, setEditionModalOpen] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [library, setLibrary] = useState([]);
  const [tplApplied, setTplApplied] = useState(null);
  const [templateRaw, setTemplateRaw] = useState("");   // raw library text with [PLACEHOLDERS]

  useEffect(() => {
    api.get("/papers")
      .then(({ data }) => setPapers(data.filter((p) => p.is_active !== false)))
      .catch((e) => setErr(formatApiError(e)))
      .finally(() => setLoading(false));
    api.get("/templates/mine").then(({ data }) => setTemplates(data)).catch(() => {});
    api.get("/templates/library").then(({ data }) => setLibrary(data)).catch(() => {});
  }, []);

  const applyTemplate = (tpl) => {
    if (!tpl) return;
    setTemplateRaw("");   // personal templates already have filled values
    update({
      paper_id: tpl.paper_id || draft.paper_id,
      language: tpl.language || draft.language,
      ad_size: tpl.ad_size || "4x10",
      ad_mode: tpl.ad_mode || "text",
      ad_text: tpl.ad_text || "",
    });
    if (tpl.advocate && Object.keys(tpl.advocate).length) {
      const a = tpl.advocate;
      updateAdvocate({
        name: a.name || draft.advocate.name,
        bar_id: a.bar_id || draft.advocate.bar_id,
        contact_primary: a.contact_primary || draft.advocate.contact_primary,
        contact_secondary: a.contact_secondary || draft.advocate.contact_secondary,
        contact_tertiary: a.contact_tertiary || draft.advocate.contact_tertiary,
        address: a.address || draft.advocate.address,
      });
    }
    setTplApplied(tpl.id);
    setErr("");
  };

  const applyLibraryTemplate = async (lib) => {
    update({
      language: lib.language || draft.language,
      ad_mode: "text",
      ad_text: lib.ad_text || "",
    });
    setTemplateRaw(lib.ad_text || "");   // unlock the placeholder editor
    setTplApplied(`lib-${lib.id}`);
    try {
      const { data } = await api.post(`/templates/library/${lib.id}/save`);
      setTemplates((t) => [data, ...t]);
    } catch {}
  };

  const deleteTemplate = async (id) => {
    try {
      await api.delete(`/templates/${id}`);
      setTemplates((ts) => ts.filter((t) => t.id !== id));
      if (tplApplied === id) setTplApplied(null);
    } catch (e) { setErr(formatApiError(e)); }
  };

  const isChangeOfName = draft.ad_type === "change_of_name";

  // Filter papers based on category — Change of Name only offered by 3 papers
  const filteredPapers = useMemo(
    () => (isChangeOfName ? papers.filter((p) => p.change_of_name_rate) : papers),
    [isChangeOfName, papers]
  );

  const selectedPaper = useMemo(
    () => papers.find((p) => p.id === draft.paper_id),
    [papers, draft.paper_id]
  );

  const wordCount = useMemo(() => {
    if (draft.ad_mode !== "text") return 0;
    return (draft.ad_text || "").trim().split(/\s+/).filter(Boolean).length;
  }, [draft.ad_text, draft.ad_mode]);

  // Auto-compose ad_text for Change of Name from the structured fields so the
  // backend still receives a well-formed body without the user typing anything.
  // NOTE: draft.ad_text is in the deps below because we WRITE to it, but the
  // effect early-exits via the "if (draft.ad_text !== composed)" guard — so
  // there is no re-entry loop. Do NOT remove that guard.
  useEffect(() => {
    if (!isChangeOfName) return;
    const on = (draft.old_name || "OLD NAME").trim().toUpperCase();
    const nn = (draft.new_name || "NEW NAME").trim().toUpperCase();
    const af = (draft.affidavit_number || "XXXXXXXX").trim();
    const dt = (draft.affidavit_date || "").trim();
    const army = (draft.army_no || "").trim();
    const rnk  = (draft.rank_label || "").trim();
    const rel  = (draft.relation || "self").toLowerCase();
    const declarant = (draft.advocate?.name || "the Declarant").trim().toUpperCase();
    const addr = (draft.advocate?.address || "").trim();
    const armyPrefix = (army || rnk)
      ? `I, ${army ? `Army No. ${army}, ` : ""}${rnk ? `Rank-${rnk}, ` : ""}Name : ${declarant}`
      : `I, ${declarant}`;
    const relSubject = rel === "self" ? "my"
      : rel === "mother" ? "my Mother"
      : rel === "father" ? "my Father"
      : rel === "child"  ? "my Child's"
      : "my";
    const addrLine = addr ? ` presently resident at ${addr},` : "";

    let composed = "";
    if (draft.con_subtype === "name_and_dob") {
      const oldDob = (draft.old_dob || "OLD DOB").trim();
      const newDob = (draft.new_dob || "NEW DOB").trim();
      composed =
        `CHANGE OF NAME AND DATE OF BIRTH — ${armyPrefix}${addrLine} ` +
        `is hereby declare that I have changed ${relSubject} name from ${on} to ${nn} ` +
        `& ${rel === "self" ? "my" : "Her/His"} Date of Birth from ${oldDob} to ${newDob}. ` +
        `Affidavit No. ${af}${dt ? ` dated ${dt}` : ""}.`;
    } else {
      composed =
        `CHANGE OF NAME — ${armyPrefix}${addrLine} ` +
        `is hereby declare that I have changed ${relSubject} name from ${on} to ${nn}. ` +
        `Affidavit No. ${af}${dt ? ` dated ${dt}` : ""}. Henceforth ${rel === "self" ? "I" : "They"} will be known as ${nn}.`;
    }
    if (draft.ad_text !== composed) update({ ad_text: composed, ad_mode: "text" });
  }, [draft.old_name, draft.new_name, draft.affidavit_number, draft.affidavit_date,
      draft.army_no, draft.rank_label, draft.relation, draft.con_subtype,
      draft.old_dob, draft.new_dob, draft.advocate?.name, draft.advocate?.address,
      isChangeOfName, draft.ad_text, update]);

  const flatRate = selectedPaper
    ? Number(isChangeOfName ? (selectedPaper.change_of_name_rate || 0) : selectedPaper.flat_rate)
    : 0;
  const discountAmount = +(flatRate * 5 / 100).toFixed(2);
  const discountedBase = +(flatRate - discountAmount).toFixed(2);
  const gstAmount = +(discountedBase * GST_PERCENT / 100).toFixed(2);
  const adMakingFee = draft.ad_mode === "make_my_ad" ? 118 : 0;
  const total = +(discountedBase + gstAmount + adMakingFee).toFixed(2);

  // Per-category cutoff (IST). Public Notice = 4 PM, Change of Name = 2 PM.
  const cutoffHourIST = isChangeOfName ? 14 : 16;
  const cutoffLabel = isChangeOfName ? "2:00 PM IST" : "4:00 PM IST";
  const isAfterCutoffIST = () => {
    const now = new Date();
    const ist = new Date(now.getTime() + (5.5 * 3600 * 1000) + (now.getTimezoneOffset() * 60 * 1000));
    return ist.getHours() >= cutoffHourIST;
  };
  const isNextDay = (dateStr) => {
    if (!dateStr) return false;
    const now = new Date();
    const ist = new Date(now.getTime() + (5.5 * 3600 * 1000) + (now.getTimezoneOffset() * 60 * 1000));
    const tomorrow = new Date(ist.getFullYear(), ist.getMonth(), ist.getDate() + 1);
    const pd = new Date(dateStr + "T00:00:00");
    return pd.getFullYear() === tomorrow.getFullYear() && pd.getMonth() === tomorrow.getMonth() && pd.getDate() === tomorrow.getDate();
  };
  const cutoffViolation = isNextDay(draft.publish_date) && isAfterCutoffIST();

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 4 * 1024 * 1024) { setErr("File must be under 4 MB"); return; }
    const dataUrl = await fileToDataUrl(file);
    update({ ad_file_name: file.name, ad_file_data_url: dataUrl });
  };

  const onAffidavitFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 4 * 1024 * 1024) { setErr("Affidavit file must be under 4 MB"); return; }
    const dataUrl = await fileToDataUrl(file);
    update({ affidavit_file_name: file.name, affidavit_data_url: dataUrl });
  };

  const onAdSizeChange = (val) => {
    if (val === "other") { setAdSizeModalOpen(true); return; } // keep prior selection
    update({ ad_size: val });
  };

  const onEditionChange = (val) => {
    if (val === "other") { setEditionModalOpen(true); return; }   // keep prior selection
    update({ edition: val });
  };

  const valid = () => {
    if (!draft.paper_id) return "Please select a newspaper";
    if (!draft.edition) return "Please select an edition";
    if (!draft.publish_date) return "Please pick a publish date";
    if (cutoffViolation) return `Bookings for tomorrow's edition close at ${cutoffLabel}. Please pick a later publish date.`;
    if (!draft.language) return "Please select the language of advertisement";
    if (!draft.ad_size) return "Please select an ad size";
    if (draft.ad_mode === "text" && (!draft.ad_text || wordCount < 5)) return "Ad text must be at least 5 words";
    if (draft.ad_mode === "upload" && !draft.ad_file_data_url) return "Please upload your ad file";
    if (isChangeOfName) {
      if (!draft.old_name?.trim()) return "Please enter your old (current) name";
      if (!draft.new_name?.trim()) return "Please enter your new name (as per Affidavit)";
      if (!draft.affidavit_number?.trim()) return "Please enter the Affidavit Number";
      if (!draft.affidavit_data_url) return "Please upload the Affidavit copy";
      if (draft.con_subtype === "name_and_dob") {
        if (!draft.new_dob?.trim()) return "Please enter the New Date of Birth";
        if (!draft.old_dob?.trim()) return "Please enter the Old Date of Birth (or reference)";
      }
    }
    if (!draft.advocate.name || draft.advocate.name.trim().length < 2) return "Your name is required";
    if (!draft.advocate.email || !/^\S+@\S+\.\S+$/.test(draft.advocate.email.trim())) return "A valid email is required (invoice + ad PDF will be sent here)";
    if (!draft.advocate.profession) return "Please select your profession";
    if (!draft.advocate.contact_primary || draft.advocate.contact_primary.trim().length < 6) return "Primary contact number is required";
    if (!draft.advocate.address || draft.advocate.address.trim().length < 10) return "Address is required (min 10 characters)";
    return null;
  };

  const onContinue = () => {
    const v = valid();
    if (v) { setErr(v); window.scrollTo({ top: 0, behavior: "smooth" }); return; }
    setErr("");
    nav("/book/checkout");
  };

  return (
    <div className="min-h-screen flex flex-col bg-parchment">
      <Navbar />
      <main className="flex-1 max-w-7xl mx-auto px-6 lg:px-10 py-12 w-full">
        <WizardSteps current="compose" />
        <div className="grid lg:grid-cols-3 gap-8">
          {/* LEFT - form */}
          <div className="lg:col-span-2 space-y-8">
            <div>
              <div className="text-[10px] tracking-overline uppercase text-gold font-bold">{t("book.step_compose")}</div>
              <h1 className="font-display text-4xl lg:text-5xl text-navy-deep mt-2">
                {isChangeOfName ? "Compose your Change of Name ad." : t("book.step_compose_title")}
              </h1>
            </div>

            {draft.ad_type === "public_notice" && (
              <div className="bg-white border border-black/15 rounded-sm p-4 sm:p-5" data-testid="composer-sample-notice">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[10px] tracking-overline uppercase text-gray-500 font-semibold">{t("book.sample_notices")}</div>
                  <div className="text-[10px] text-gray-400">Reference</div>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {SAMPLE_NOTICE_IMAGES.map((img) => {
                    const cap = img.label === "English" ? t("home.sample_english")
                      : img.label === "Marathi" ? t("home.sample_marathi")
                      : "Change of Name";
                    return (
                      <figure key={img.label} className="flex flex-col items-center" data-testid={`composer-sample-${img.label.toLowerCase().replace(/\s+/g, '-')}`}>
                        <img src={img.src} alt={img.alt}
                             className="w-full h-auto rounded-sm border border-black/15"
                             style={{ maxHeight: 200, objectFit: "contain" }}
                             loading="lazy" />
                        <figcaption className="text-[10px] tracking-overline uppercase text-gray-500 font-semibold mt-1 text-center leading-tight">
                          {cap}
                        </figcaption>
                      </figure>
                    );
                  })}
                </div>
                <p className="text-[11px] text-gray-500 mt-2 italic text-center">{t("book.sample_footnote")}</p>
              </div>
            )}

            {err && <div className="px-4 py-3 bg-red-50 border-l-4 border-red-500 text-red-700 text-sm" data-testid="composer-error">{err}</div>}

            {/* Welworth Library (curated starter notices) — Public Notice only */}
            {!isChangeOfName && library.length > 0 && (
              <div className="bg-white border border-black/15 rounded-sm p-4 sm:p-5" data-testid="library-picker">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[10px] tracking-overline uppercase text-gray-500 font-semibold">Notice Library · curated by Welworth</div>
                  <div className="text-xs text-gray-500">tap to prefill</div>
                </div>
                <div className="flex flex-wrap gap-2">
                  {library.map((l) => {
                    const active = tplApplied === `lib-${l.id}`;
                    return (
                      <button
                        type="button"
                        key={l.id}
                        onClick={() => applyLibraryTemplate(l)}
                        className={`px-3 py-1.5 border rounded-sm text-xs transition ${active ? "border-black bg-black text-white" : "border-black/20 bg-white text-black hover:border-black"}`}
                        data-testid={`library-${l.category}`}
                        title={`Insert the "${l.name}" starter draft into the Ad Text field`}
                      >
                        {l.name}
                      </button>
                    );
                  })}
                </div>
                <p className="text-xs text-gray-500 mt-2">Replace the <span className="font-mono">[BRACKETED]</span> placeholders with your client&apos;s details before publishing.</p>
              </div>
            )}

            {!isChangeOfName && templates.length > 0 && (
              <div className="bg-white border border-black/15 rounded-sm p-4 sm:p-5" data-testid="templates-picker">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[10px] tracking-overline uppercase text-gray-500 font-semibold">Your Saved Templates</div>
                  <div className="text-xs text-gray-500">{templates.length} saved</div>
                </div>
                <div className="flex flex-wrap gap-2">
                  {templates.map((t) => {
                    const active = tplApplied === t.id;
                    return (
                      <div key={t.id} className={`group flex items-center gap-1 border rounded-sm transition ${active ? "border-black bg-black text-white" : "border-black/20 bg-white text-black hover:border-black"}`} data-testid={`template-chip-${t.id}`}>
                        <button
                          type="button"
                          onClick={() => applyTemplate(t)}
                          className="pl-3 pr-2 py-1.5 text-xs flex items-center gap-1.5"
                          title={t.name}
                          data-testid={`template-apply-${t.id}`}
                        >
                          <BookmarkCheck size={12}/>{t.name}
                        </button>
                        <button
                          type="button"
                          onClick={() => deleteTemplate(t.id)}
                          className={`pr-2 pl-1 py-1.5 ${active ? "text-white/70 hover:text-white" : "text-gray-400 hover:text-red-500"}`}
                          aria-label="Delete template"
                          data-testid={`template-delete-${t.id}`}
                        >
                          <Trash2 size={12}/>
                        </button>
                      </div>
                    );
                  })}
                </div>
                {tplApplied && <p className="text-xs text-gray-500 mt-2">Template applied — review and edit any field before continuing.</p>}
              </div>
            )}

            {cutoffViolation && !err && (
              <div className="px-4 py-3 bg-amber-50 border-l-4 border-amber-500 text-amber-800 text-sm" data-testid="cutoff-warning">
                <strong>Cutoff missed:</strong> bookings for tomorrow&apos;s edition close at <strong>{cutoffLabel}</strong>. Please pick a later publish date.
              </div>
            )}

            <div className="px-4 py-3 bg-yellow-100 border-l-4 border-yellow-500 rounded-sm" data-testid="cutoff-banner">
              <div className="text-[10px] tracking-overline uppercase font-bold text-yellow-800">
                Daily Booking Cut-off · {isChangeOfName ? "Change of Name" : "Public Notice"}
              </div>
              <div className="text-sm sm:text-base font-bold text-yellow-900 mt-0.5">
                Bookings close at <span className="text-lg">{cutoffLabel}</span> for next-day publication.
              </div>
              <div className="text-xs text-yellow-800 mt-1">
                Place your order before {cutoffLabel} to appear in tomorrow&apos;s edition.
              </div>
            </div>

            <div className="px-4 py-3 bg-gray-50 border border-black/10 rounded-sm text-xs sm:text-sm text-gray-700" data-testid="rate-scope-note">
              {isChangeOfName ? (
                <>The rates shown apply to <strong>3 × 5</strong> Change of Name ads in the <strong>Chh. Sambhajinagar edition</strong> only, and are offered by <strong>Times of India</strong>, <strong>Maharashtra Times</strong> and <strong>Lokmat</strong>. For other editions / cities, please contact us directly.</>
              ) : (
                <Trans i18nKey="book.rate_scope_note" components={{ strong: <strong /> }} />
              )}
            </div>

            {/* Paper + Date + Language + Size */}
            <div className="bg-white border border-slate-200 rounded-sm p-7 space-y-5">
              <div className="text-[10px] tracking-overline uppercase text-gold font-bold">Publication</div>
              <div className="grid sm:grid-cols-2 gap-5">
                <Field label="Newspaper" required testid="paper">
                  <select
                    value={draft.paper_id}
                    onChange={(e) => update({ paper_id: e.target.value })}
                    className="w-full px-4 py-3 border border-slate-300 focus:border-navy focus:ring-1 focus:ring-navy outline-none rounded-sm bg-white text-navy-deep"
                    data-testid="composer-paper-select"
                  >
                    <option value="">{loading ? "Loading…" : "Select a newspaper"}</option>
                    {filteredPapers.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name} · {p.language}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="Edition" required hint="Rates shown apply to Chh. Sambhajinagar edition only." testid="edition">
                  <select
                    value={draft.edition || ""}
                    onChange={(e) => onEditionChange(e.target.value)}
                    className="w-full px-4 py-3 border border-slate-300 focus:border-navy focus:ring-1 focus:ring-navy outline-none rounded-sm bg-white text-navy-deep"
                    data-testid="composer-edition-select"
                  >
                    <option value="">Select edition</option>
                    <option value="sambhajinagar">Chh. Sambhajinagar</option>
                    <option value="other">Other edition / city…</option>
                  </select>
                </Field>
                <Field label="Publish Date" required testid="publish-date">
                  <TextInput
                    type="date"
                    value={draft.publish_date}
                    onChange={(v) => update({ publish_date: v })}
                    testid="composer-publish-date"
                    min={new Date(Date.now() + 24*3600*1000).toISOString().split("T")[0]}
                  />
                </Field>
                <Field label="Language of Advertisement" required testid="language">
                  <select
                    value={draft.language || ""}
                    onChange={(e) => update({ language: e.target.value })}
                    className="w-full px-4 py-3 border border-slate-300 focus:border-navy focus:ring-1 focus:ring-navy outline-none rounded-sm bg-white text-navy-deep"
                    data-testid="composer-language-select"
                  >
                    <option value="">Select language</option>
                    <option value="english">English</option>
                    <option value="marathi">Marathi</option>
                    <option value="hindi">Hindi</option>
                  </select>
                </Field>
                <Field label="Ad Size" required hint={isChangeOfName ? "Fixed 3 × 5 cm for Change of Name ads." : "More sizes available — pick 'Other' to contact us."} testid="ad-size">
                  <select
                    value={draft.ad_size || ""}
                    onChange={(e) => onAdSizeChange(e.target.value)}
                    className="w-full px-4 py-3 border border-slate-300 focus:border-navy focus:ring-1 focus:ring-navy outline-none rounded-sm bg-white text-navy-deep"
                    data-testid="composer-ad-size-select"
                  >
                    {isChangeOfName ? (
                      <option value="3x5">3 cm × 5 cm  ·  15 sq cm  (standard)</option>
                    ) : (
                      <>
                        <option value="">Select ad size</option>
                        <option value="4x10">4 cm × 10 cm  ·  40 sq cm  (standard)</option>
                        <option value="other">Other size…</option>
                      </>
                    )}
                  </select>
                </Field>
              </div>
            </div>

            {/* Ad mode toggle — hidden for Change of Name (auto-composed from CoN fields) */}
            {!isChangeOfName && (
            <div className="bg-white border border-slate-200 rounded-sm p-7 space-y-5">
              <div className="flex items-center justify-between">
                <div className="text-[10px] tracking-overline uppercase text-gold font-bold">Your Ad Content</div>
                {draft.ad_mode === "text" && (
                  <div className="text-xs font-mono text-slate-500" data-testid="word-count">
                    {wordCount} words
                  </div>
                )}
              </div>

              <div className="grid sm:grid-cols-3 gap-3" data-testid="ad-mode-radios">
                {[
                  { v: "text", label: "Type / Paste text", icon: Type },
                  { v: "upload", label: "Upload file", icon: Upload },
                  { v: "make_my_ad", label: "Make my Ad · ₹118", icon: Sparkles },
                ].map((opt) => {
                  const Ic = opt.icon;
                  const active = draft.ad_mode === opt.v;
                  return (
                    <button
                      type="button"
                      key={opt.v}
                      onClick={() => update({ ad_mode: opt.v })}
                      className={`p-4 border rounded-sm text-left transition flex items-start gap-3 ${
                        active
                          ? "border-navy bg-navy text-parchment"
                          : "border-slate-300 bg-white text-navy-deep hover:border-gold"
                      }`}
                      data-testid={`ad-mode-${opt.v}`}
                    >
                      <Ic size={18} className={active ? "text-gold" : "text-slate-500"}/>
                      <div className="text-sm font-semibold leading-snug">{opt.label}</div>
                    </button>
                  );
                })}
              </div>

              {draft.ad_mode === "text" && (
                <>
                  {templateRaw && (
                    <PlaceholderEditor
                      templateRaw={templateRaw}
                      onBodyChange={(newBody) => update({ ad_text: newBody })}
                      onClear={() => setTemplateRaw("")}
                    />
                  )}
                  <Field label="Ad Text" required hint="Tip: include survey numbers, full names, and dates clearly." testid="ad-text">
                    <TextArea
                      rows={10}
                      value={draft.ad_text}
                      onChange={(v) => { update({ ad_text: v }); if (templateRaw) setTemplateRaw(""); }}
                      placeholder="PUBLIC NOTICE — Notice is hereby given that my client…"
                      testid="composer-ad-text"
                    />
                  </Field>
                </>
              )}

              {draft.ad_mode === "upload" && (
                <Field label="Upload Ad File (PDF / PNG / JPG)" required hint="Max 4 MB. Use camera on mobile to capture handwritten draft.">
                  {draft.ad_file_name ? (
                    <div className="flex items-center justify-between px-4 py-3 border border-slate-300 rounded-sm bg-slate-50" data-testid="ad-file-preview">
                      <div className="flex items-center gap-3 text-sm">
                        <FileText size={18} className="text-gold"/> <span className="font-mono">{draft.ad_file_name}</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => update({ ad_file_name: "", ad_file_data_url: "" })}
                        className="text-slate-400 hover:text-red-500"
                        data-testid="ad-file-remove"
                      ><X size={16}/></button>
                    </div>
                  ) : (
                    <input
                      type="file"
                      accept="application/pdf,image/png,image/jpeg"
                      capture="environment"
                      onChange={onFile}
                      className="block w-full text-sm file:mr-4 file:px-4 file:py-3 file:border-0 file:bg-navy file:text-parchment file:rounded-sm file:font-semibold file:cursor-pointer hover:file:bg-gold hover:file:text-navy-deep transition border border-dashed border-slate-300 rounded-sm py-3 px-3 bg-slate-50"
                      data-testid="composer-ad-file"
                    />
                  )}
                </Field>
              )}

              {draft.ad_mode === "make_my_ad" && (
                <div className="p-5 bg-parchment border-l-4 border-gold" data-testid="make-my-ad-note">
                  <div className="font-display text-xl text-navy-deep">Our copyist will draft your notice.</div>
                  <p className="text-sm text-slate-600 mt-1 leading-relaxed">A flat <span className="font-mono font-bold">₹118 (incl. 18% GST on ad-making)</span> will be added to your invoice. We&apos;ll email a draft for your approval before publishing — typically within 2 working hours.</p>
                </div>
              )}
            </div>
            )}

            {/* Change-of-Name specific fields */}
            {isChangeOfName && (
              <div className="bg-white border-2 border-black rounded-sm p-6 space-y-5" data-testid="change-of-name-section">
                <div className="grid md:grid-cols-3 gap-5">
                  <div className="md:col-span-2 space-y-1">
                    <div className="text-[10px] tracking-overline uppercase text-black font-bold">Change of Name · Required</div>
                    <h3 className="font-display text-xl text-black">Sub-category &amp; Affidavit details</h3>
                    <p className="text-xs text-slate-600">Pick whether you are changing only the name, or the name AND date of birth in a single ad.</p>
                  </div>
                  {/* Sample reference — CoN format only */}
                  <figure className="border border-black/15 rounded-sm p-2 bg-slate-50" data-testid="con-sample-reference">
                    <img
                      src={draft.con_subtype === "name_and_dob" ? "/img/samples/sample-change-of-name-dob.jpeg" : "/img/samples/sample-change-of-name.jpeg"}
                      alt="Sample Change of Name notice"
                      className="w-full h-auto object-contain" style={{ maxHeight: 160 }} loading="lazy"/>
                    <figcaption className="text-[9px] tracking-overline uppercase text-gray-500 text-center mt-1 font-semibold">Sample · 3 × 5 cm</figcaption>
                  </figure>
                </div>

                {/* Sub-category picker */}
                <Field label="Sub-category" required testid="con-subtype">
                  <select
                    value={draft.con_subtype || "name_only"}
                    onChange={(e) => update({ con_subtype: e.target.value })}
                    className="w-full px-4 py-3 border border-slate-300 focus:border-navy focus:ring-1 focus:ring-navy outline-none rounded-sm bg-white text-navy-deep"
                    data-testid="composer-con-subtype"
                  >
                    <option value="name_only">Change of Name only</option>
                    <option value="name_and_dob">Change of Name AND Date of Birth (single ad)</option>
                  </select>
                </Field>

                {/* Army No & Rank (optional but published as-is if filled) */}
                <div className="grid sm:grid-cols-3 gap-5">
                  <Field label="Army No." hint="Optional — included in the ad if provided" testid="con-army-no">
                    <TextInput value={draft.army_no} onChange={(v) => update({ army_no: v })} placeholder="e.g. 2783472L" testid="composer-army-no"/>
                  </Field>
                  <Field label="Rank" hint="Optional — e.g. EX-HAV, SUB, HAV" testid="con-rank">
                    <TextInput value={draft.rank_label} onChange={(v) => update({ rank_label: v })} placeholder="EX-HAV" testid="composer-rank"/>
                  </Field>
                  <Field label="Whose details are changing?" testid="con-relation">
                    <select
                      value={draft.relation || "self"}
                      onChange={(e) => update({ relation: e.target.value })}
                      className="w-full px-4 py-3 border border-slate-300 focus:border-navy focus:ring-1 focus:ring-navy outline-none rounded-sm bg-white text-navy-deep"
                      data-testid="composer-relation"
                    >
                      <option value="self">Self (my own)</option>
                      <option value="mother">Mother</option>
                      <option value="father">Father</option>
                      <option value="child">Child</option>
                    </select>
                  </Field>
                </div>

                <div className="grid sm:grid-cols-2 gap-5">
                  <Field label={draft.relation === "self" ? "Old Name (as per current records)" : "Old Name (of the person)"} required testid="con-old-name">
                    <TextInput value={draft.old_name} onChange={(v) => update({ old_name: v })} placeholder="e.g. SULOCHANA" testid="composer-old-name"/>
                  </Field>
                  <Field label="New Name (as per Affidavit)" required testid="con-new-name">
                    <TextInput value={draft.new_name} onChange={(v) => update({ new_name: v })} placeholder="e.g. SULOCHANA PANDURANG SHELKE" testid="composer-new-name"/>
                  </Field>
                </div>

                {/* DOB fields — only for name_and_dob subtype */}
                {draft.con_subtype === "name_and_dob" && (
                  <div className="grid sm:grid-cols-2 gap-5" data-testid="con-dob-fields">
                    <Field label="Old Date of Birth (or reference)" required hint="Include existing records e.g. '64 YRS (as per Disch Book)' or 10-06-1948">
                      <TextInput value={draft.old_dob} onChange={(v) => update({ old_dob: v })} placeholder="10-06-1948 (as per ECHS Card)" testid="composer-old-dob"/>
                    </Field>
                    <Field label="New Date of Birth" required hint="Corrected date as per Affidavit — dd-mm-yyyy">
                      <TextInput value={draft.new_dob} onChange={(v) => update({ new_dob: v })} placeholder="01-01-1951" testid="composer-new-dob"/>
                    </Field>
                  </div>
                )}

                <div className="grid sm:grid-cols-2 gap-5">
                  <Field label="Affidavit Number" required testid="con-affidavit-no">
                    <TextInput value={draft.affidavit_number} onChange={(v) => update({ affidavit_number: v })} placeholder="e.g. 85AB 547993" testid="composer-affidavit-number"/>
                  </Field>
                  <Field label="Affidavit Date" hint="Date on the Affidavit (dd-mm-yyyy)" testid="con-affidavit-date">
                    <TextInput value={draft.affidavit_date} onChange={(v) => update({ affidavit_date: v })} placeholder="13-07-2026" testid="composer-affidavit-date"/>
                  </Field>
                </div>

                <Field label="Upload Affidavit Copy" required hint="PDF, JPG or PNG · max 4 MB · used for verification only, not published.">
                  {draft.affidavit_file_name ? (
                    <div className="flex items-center justify-between px-4 py-3 border border-slate-300 rounded-sm bg-slate-50" data-testid="affidavit-file-preview">
                      <div className="flex items-center gap-3 text-sm">
                        <FileText size={18} className="text-black"/> <span className="font-mono">{draft.affidavit_file_name}</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => update({ affidavit_file_name: "", affidavit_data_url: "" })}
                        className="text-slate-400 hover:text-red-500"
                        data-testid="affidavit-file-remove"
                      ><X size={16}/></button>
                    </div>
                  ) : (
                    <input
                      type="file"
                      accept="application/pdf,image/png,image/jpeg"
                      capture="environment"
                      onChange={onAffidavitFile}
                      className="block w-full text-sm file:mr-4 file:px-4 file:py-3 file:border-0 file:bg-black file:text-white file:rounded-sm file:font-semibold file:cursor-pointer hover:file:bg-gray-800 transition border border-dashed border-slate-300 rounded-sm py-3 px-3 bg-slate-50"
                      data-testid="composer-affidavit-file"
                    />
                  )}
                </Field>

                <div className="px-4 py-3 bg-yellow-50 border-l-4 border-yellow-500 text-yellow-900 text-xs sm:text-sm" data-testid="change-of-name-disclaimer">
                  <strong>Disclaimer:</strong> This ad will be published subject to verification. Our team will verify the Affidavit copy before publication. Fraudulent submissions will be reported to the concerned newspaper and to law enforcement.
                </div>
              </div>
            )}

            {/* Your details (advocate / business / individual) */}
            <div className="bg-white border border-slate-200 rounded-sm p-7 space-y-5">
              <div className="text-[10px] tracking-overline uppercase text-gold font-bold">Your Details</div>
              <p className="text-xs text-slate-500 -mt-2">Open to advocates, businesses, banks, builders &amp; individuals.</p>
              <div className="grid sm:grid-cols-2 gap-5">
                <Field label="Full Name" required testid="advocate-name">
                  <TextInput value={draft.advocate.name} onChange={(v) => updateAdvocate({ name: v })} placeholder="e.g. Anjali Patil" testid="composer-advocate-name" autoComplete="name"/>
                </Field>
                <Field label="Email Address" required hint="Your invoice + ad PDF will be emailed here." testid="advocate-email">
                  <TextInput type="email" value={draft.advocate.email} onChange={(v) => updateAdvocate({ email: v })} placeholder="you@example.com" testid="composer-advocate-email" autoComplete="email"/>
                </Field>
                <Field label="Profession" required testid="profession">
                  <select
                    value={draft.advocate.profession || ""}
                    onChange={(e) => updateAdvocate({ profession: e.target.value })}
                    className="w-full px-4 py-3 border border-slate-300 focus:border-navy focus:ring-1 focus:ring-navy outline-none rounded-sm bg-white text-navy-deep"
                    data-testid="composer-profession-select"
                  >
                    <option value="">Select your profession</option>
                    {PROFESSIONS.map((p) => <option key={p} value={p}>{p}</option>)}
                  </select>
                </Field>
                {draft.advocate.profession === "Advocate" && (
                  <Field label="Bar Council ID" hint="Optional" testid="bar-id">
                    <TextInput value={draft.advocate.bar_id} onChange={(v) => updateAdvocate({ bar_id: v })} placeholder="MH/2014/3892" testid="composer-bar-id"/>
                  </Field>
                )}
              </div>

              <div>
                <div className="text-[10px] tracking-overline uppercase font-bold text-slate-600 mb-2">Contact No. <span className="text-red-500">*</span></div>
                <div className="grid sm:grid-cols-3 gap-3" data-testid="advocate-contacts">
                  <TextInput value={draft.advocate.contact_primary} onChange={(v) => updateAdvocate({ contact_primary: v })} placeholder="Primary (required)" testid="composer-contact-1" inputMode="tel" autoComplete="tel"/>
                  <TextInput value={draft.advocate.contact_secondary} onChange={(v) => updateAdvocate({ contact_secondary: v })} placeholder="Secondary (optional)" testid="composer-contact-2" inputMode="tel"/>
                  <TextInput value={draft.advocate.contact_tertiary} onChange={(v) => updateAdvocate({ contact_tertiary: v })} placeholder="Tertiary (optional)" testid="composer-contact-3" inputMode="tel"/>
                </div>
                <p className="text-xs text-slate-500 mt-1.5">Only the first number is mandatory.</p>
              </div>

              <Field label="Address" required hint="Office / Chamber / Residence address for the records." testid="advocate-address">
                <TextArea
                  rows={4}
                  value={draft.advocate.address}
                  onChange={(v) => updateAdvocate({ address: v })}
                  placeholder="Office No. 14, Annex Building, Kranti Chowk, Chh. Sambhajinagar – 431001"
                  testid="composer-advocate-address"
                />
              </Field>
            </div>
          </div>

          {/* RIGHT - sticky invoice */}
          <div className="lg:col-span-1">
            <div className="lg:sticky lg:top-24 bg-white border border-slate-200 rounded-sm p-6 space-y-4">
              <div className="text-[10px] tracking-overline uppercase text-gold font-bold">Live Estimate</div>
              <div className="text-sm space-y-2.5 font-mono">
                <div className="flex justify-between"><span className="text-gray-500">Category</span><span>{isChangeOfName ? "Change of Name" : "Public Notice"}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Paper</span><span className="text-right">{selectedPaper?.name || "—"}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Language</span><span className="capitalize">{draft.language || "—"}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Ad size</span><span>{isChangeOfName ? "3 × 5 cm" : "4 × 10 cm"}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Flat rate</span><span>₹{flatRate.toFixed(2)}</span></div>
                <div className="flex justify-between text-emerald-700" data-testid="estimate-discount-line"><span>– Online discount (5%)</span><span>– ₹{discountAmount.toFixed(2)}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">GST (5%, on discounted base)</span><span>₹{gstAmount.toFixed(2)}</span></div>
                {adMakingFee > 0 && <div className="flex justify-between text-black"><span>+ Make-my-Ad</span><span>₹{adMakingFee.toFixed(2)}</span></div>}
                <div className="border-t border-black/15 pt-3 flex justify-between text-lg font-bold text-black">
                  <span>Total</span><span data-testid="estimate-total">₹{total.toFixed(2)}</span>
                </div>
              </div>
              <button
                onClick={onContinue}
                className="w-full bg-navy text-parchment py-3.5 rounded-sm hover:bg-gold hover:text-navy-deep transition font-semibold"
                data-testid="composer-continue"
              >Continue to Checkout →</button>
              <p className="text-xs text-slate-500">Pricing locks in at checkout.</p>
            </div>
          </div>
        </div>
      </main>
      <Footer />

      {/* Other-size popup */}
      <ComingSoonModal
        open={adSizeModalOpen}
        onClose={() => setAdSizeModalOpen(false)}
        adTypeLabel="Custom Ad Sizes"
        badge="Contact Us"
        message="Custom ad sizes are available — please contact us on these numbers to book a non-standard size. Our team will quote and process your booking manually."
      />

      {/* Other-edition popup */}
      <ComingSoonModal
        open={editionModalOpen}
        onClose={() => setEditionModalOpen(false)}
        adTypeLabel="Other Editions & Cities"
        badge="Coming Soon"
        message="Other editions and cities will be added soon. Till then, please contact us on the given numbers and our team will process your booking manually."
      />
    </div>
  );
}
